import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { db } from "../contexts/firebase/config";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
  const auth = getAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [storeName, setStoreName] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      localStorage.setItem("darkMode", JSON.stringify(!prev));
      return !prev;
    });
  };

  const ADMIN_EMAILS = [
    "sintanetcomissoes@gmail.com",
    "gonxnegocios@gmail.com",
    "geral@sintanet.pt",
  ];

  const LOJA_INFO = {
    "lojataipas@sintanet.com": "Loja Taipas",
    "lojapovoa@sintanet.com": "Loja Póvoa",
    "lojamares@sintanet.com": "Loja Amares",
    "lojaarco@sintanet.com": "Loja Arco Baúlhe",
    "lojalousada@sintanet.com": "Loja Lousada",
    "lojataipas@gonx.com": "Loja Taipas",
    "lojaguimaraes@gonx.com": "Loja Guimarães",
    "lojapovoalanhoso@gonx.com": "Loja Póvoa de Lanhoso",
    "lojazurem@gonx.com": "Loja Azurém",
  };

  const LOJA_EMAILS = Object.keys(LOJA_INFO);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setCurrentUser(user);

      try {
        if (!user) {
          setUserType(null);
          setStoreName(null);
          return;
        }

        const email = (user.email || "").toLowerCase();

        if (ADMIN_EMAILS.includes(email)) {
          setUserType("admin");
          setStoreName(null);
          return;
        }

        if (LOJA_EMAILS.includes(email)) {
          setUserType("loja");
          setStoreName(LOJA_INFO[email] || email);
          return;
        }

        // 1. VERIFICA PRIMEIRO SE EXISTE EMPRESA COM ESTE UID
        // Isto força sempre parceiro quando existir empresas/{uid}
        const empresaDocRef = doc(db, "empresas", user.uid);
        const empresaSnap = await getDoc(empresaDocRef);

        if (empresaSnap.exists()) {
          await setDoc(
            doc(db, "users", user.uid),
            {
              uid: user.uid,
              email,
              nome: user.displayName || "",
              role: "partner",
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          setUserType("partner");
          setStoreName(null);
          return;
        }

        // 2. SÓ DEPOIS VERIFICA users/{uid}
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          const role = data.role || null;

          if (role === "partner" || role === "parceiro") {
            setUserType("partner");
            setStoreName(null);
          } else if (role === "loja") {
            setUserType("loja");
            setStoreName(data.storeName || null);
          } else if (role === "admin") {
            setUserType("admin");
            setStoreName(null);
          } else {
            setUserType("cliente");
            setStoreName(null);
          }

          return;
        }

        // 3. PRIMEIRO LOGIN GOOGLE SEM PERFIL
        await setDoc(
          doc(db, "users", user.uid),
          {
            uid: user.uid,
            email,
            nome: user.displayName || "",
            phone: user.phoneNumber || null,
            empresa: null,
            role: "cliente",
            provider: "google",
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );

        setUserType("cliente");
        setStoreName(null);
      } catch (err) {
        console.error("Erro ao determinar userType:", err);
        setUserType(null);
        setStoreName(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [auth]);

  const signup = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const login = async (email, password, options = {}) => {
    const persistence = options.remember
      ? browserLocalPersistence
      : browserSessionPersistence;

    await setPersistence(auth, persistence);
    return signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async (options = {}) => {
    const persistence = options.remember
      ? browserLocalPersistence
      : browserSessionPersistence;

    await setPersistence(auth, persistence);

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    return signInWithPopup(auth, provider);
  };

  const logout = () => signOut(auth);

  const registerClient = async (profileData, password) => {
    if (!profileData || !profileData.email || !password) {
      throw new Error("Dados insuficientes para registo.");
    }

    const email = String(profileData.email).toLowerCase().trim();

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    const user = userCredential.user;

    try {
      await updateProfile(user, { displayName: profileData.nome || "" });
    } catch (err) {
      console.warn("updateProfile falhou:", err);
    }

    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email,
      nome: profileData.nome || "",
      empresa: profileData.empresa || null,
      phone: profileData.phone || null,
      role: "cliente",
      createdAt: serverTimestamp(),
    });

    return user;
  };

  const value = {
    currentUser,
    userType,
    storeName,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    registerClient,
    isDarkMode,
    toggleTheme,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}